<template>
  <div class="becomeAgency-wrapper ba-refresh sk-dark">
    <div class="ba-inner">

      <!-- ===== Who is inviting ===== -->
      <div class="ba-hero">
        <span class="ba-badge">Agency invite</span>
        <h1 class="ba-title">Apply to become an Agent</h1>
        <p class="ba-lede">This agency has invited you to join them</p>
      </div>

      <div class="ba-agency">
        <!-- While the invite is being fetched the card shows its own shape,
             not the word "Loading" in place of the agency's name. -->
        <template v-if="loadingAgency">
          <span class="sk sk-circle" style="flex: 0 0 44px; height: 44px"></span>
          <div class="ba-agencyBody">
            <span class="sk" style="width: 54%; height: 15px"></span>
            <span class="sk" style="width: 34%; height: 11px; margin-top: 6px"></span>
          </div>
        </template>
        <template v-else>
          <span class="ba-agencyAvatar">{{ agencyInitial }}</span>
          <div class="ba-agencyBody">
            <div class="ba-agencyName">{{ agencyName }}</div>
            <div class="ba-agencyMeta ba-num" :class="{ 'is-missing': !agencyData?.profile_id }">
              {{ agencyMeta }}
            </div>
          </div>
        </template>
      </div>

      <!-- ===== Where the host is in the flow ===== -->
      <div class="ba-steps">
        <div v-for="step in steps" :key="step.no" class="ba-step"
          :class="{ 'is-on': currentStep === step.no, 'is-done': currentStep > step.no }">
          <span class="ba-stepDot">
            <svg v-if="currentStep > step.no" width="11" height="11" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12.5l4.5 4.5L19 7" />
            </svg>
            <template v-else>{{ step.no }}</template>
          </span>
          <span class="ba-stepLabel">{{ step.label }}</span>
          <span v-if="step.no < 3" class="ba-stepLine" :class="{ 'is-done': currentStep > step.no }"></span>
        </div>
      </div>

      <!-- ===== The form ===== -->
      <div class="ba-card">

        <!-- Starmate ID -->
        <div class="ba-field">
          <label class="ba-label" for="ba-userId"><i>*</i> Starmate ID</label>
          <div class="ba-inputWrap">
            <!-- Numeric only, paste-safe -->
            <input id="ba-userId" type="text" class="ba-input" :class="{ 'is-invalid': !!userError }"
              placeholder="Enter your Starmate ID" v-model="user_profile_id" inputmode="numeric" pattern="\d*"
              maxlength="15" @input="onUserIdInput" @keydown.enter.prevent="fetchUserDetails" />
          </div>
          <small class="ba-hint" :class="{ 'is-error': !!userError }">
            <template v-if="userLoading">Checking user…</template>
            <template v-else-if="userError">{{ userError }}</template>
            <template v-else-if="!userData">At least 8 digits</template>
          </small>
        </div>

        <!-- Matched user -->
        <div v-if="userData" class="ba-user">
          <span class="ba-userImg">
            <img :src="userImageToShow" @error="onUserImgError" alt="" />
          </span>
          <div class="ba-userBody">
            <div class="ba-userName">{{ userData.name }}</div>
            <div class="ba-userId ba-num">ID: {{ userData.profile_id }}</div>
          </div>
          <svg class="ba-userCheck" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2E9E57"
            stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M8 12.4l2.6 2.6L16 9.6" />
          </svg>
        </div>

        <!-- Verification code -->
        <div class="ba-field">
          <label class="ba-label" for="ba-otp"><i>*</i> Starmate verification code</label>
          <div class="ba-inputWrap">
            <input id="ba-otp" type="text" class="ba-input has-action" placeholder="6-digit code"
              v-model.trim="verification_code" inputmode="numeric" autocomplete="one-time-code" maxlength="6"
              @input="onOtpInput" />
            <button type="button" class="ba-otpBtn" :disabled="disableOtpBtn" @click="getVerificationCode">
              {{ otpBtnLabel }}
            </button>
          </div>
          <small class="ba-hint">
            <template v-if="otpTimer > 0">Code sent — check your Starmate system messages</template>
            <template v-else-if="!userData">Enter a valid Starmate ID first</template>
          </small>
        </div>

        <div class="ba-support">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b95a4" stroke-width="2.1"
            stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5.2" />
            <path d="M12 7.6h.01" />
          </svg>
          <span>Starmate official BD will contact your WhatsApp number for support.</span>
        </div>

        <button type="button" class="ba-submit" :disabled="submitLoading || !canSubmit" @click="handleSubmit">
          {{ submitLoading ? "Submitting…" : "Submit application" }}
        </button>

        <div v-if="submitError" class="ba-banner is-error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
            stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7.5v5.5" />
            <path d="M12 16.4h.01" />
          </svg>
          <span>{{ submitError }}</span>
        </div>
        <div v-else-if="submitSuccess" class="ba-banner is-success">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
            stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M8 12.4l2.6 2.6L16 9.6" />
          </svg>
          <span>{{ submitSuccess }}</span>
        </div>
      </div>

      <!-- ===== How to get the code ===== -->
      <h2 class="ba-guideHd">How to get your verification code?</h2>
      <p class="ba-guideSub">Three steps, takes about a minute</p>

      <div class="ba-guide">

        <!-- 1 — where to tap -->
        <div class="ba-guideStep">
          <span class="ba-guideNo">1</span>
          <div class="ba-guideBody">
            <b>Tap Send code</b>
            <p>Enter your Starmate ID first, then tap Send code next to the verification field.</p>
            <div class="ba-mock">
              <div class="ba-mockLabel"><i>*</i> Starmate verification code</div>
              <div class="ba-mockField">
                <span class="ba-mockText">verification code</span>
                <span class="ba-mockBtn">Send code</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 2 — which app to open -->
        <div class="ba-guideStep">
          <span class="ba-guideNo">2</span>
          <div class="ba-guideBody">
            <b>Open the Starmate app</b>
            <p>The code arrives inside the app, not by SMS.</p>
            <div class="ba-mock ba-apps">
              <span class="ba-app">
                <span class="ba-appTile is-photos">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
                    <circle cx="8.7" cy="10" r="1.6" />
                    <path d="M4.5 17l4.8-4.6L14 17" />
                    <path d="M13.5 14.4l2.6-2.4 3.4 3.2" />
                  </svg>
                </span>
                <em>Photos</em>
              </span>
              <span class="ba-app">
                <span class="ba-appTile is-play">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff">
                    <path d="M6 3.6l12.4 8.4L6 20.4z" />
                  </svg>
                </span>
                <em>Play Store</em>
              </span>
              <span class="ba-app is-target">
                <span class="ba-appTile is-starmate">
                  <img :src="starmateLogo" alt="Starmate" />
                </span>
                <em>Starmate</em>
              </span>
              <span class="ba-app">
                <span class="ba-appTile is-browser">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2">
                    <circle cx="12" cy="12" r="8.6" />
                    <ellipse cx="12" cy="12" rx="3.8" ry="8.6" />
                    <path d="M3.6 12h16.8" />
                  </svg>
                </span>
                <em>Browser</em>
              </span>
            </div>
          </div>
        </div>

        <!-- 3 — where the code lands -->
        <div class="ba-guideStep">
          <span class="ba-guideNo">3</span>
          <div class="ba-guideBody">
            <b>Check System Messages</b>
            <p>Open System Message in the app to read your 6-digit code, then enter it here.</p>
            <div class="ba-mock ba-msg">
              <span class="ba-msgIcon">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 9a6 6 0 10-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9z" />
                  <path d="M13.7 19a2 2 0 01-3.4 0" />
                </svg>
              </span>
              <div class="ba-msgBody">
                <div class="ba-msgTop">
                  <b>System Message</b>
                  <span>01:05 pm</span>
                </div>
                <p>Your verification code is <em class="ba-num">482916</em></p>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
import "./Become_agencyStyle.css";
import "@/assets/css/skeleton.css";
import "./BecomeAgencyRefresh.css";
import defaultUserImage from "../../assets/images/default_profile.webp";
import starmateLogo from "../../assets/images/starmate-logo.webp";
import { inviteAgencyApi } from "../../services/inviteAgencyApi";

export default {
  name: "Become_agency",

  data() {
    return {
      defaultUserImage,
      // The three guide illustrations are drawn in markup rather than shipped
      // as the old 282 KB screenshot collage. Only the real app icon is an
      // image, because that is the thing a host has to recognise on their
      // home screen.
      starmateLogo,

      steps: [
        { no: 1, label: "Your ID" },
        { no: 2, label: "Verify" },
        { no: 3, label: "Submit" },
      ],

      agency_profile_id: null,
      agencyData: null,
      loadingAgency: false,

      // user lookup
      user_profile_id: "",
      userData: null,
      userImgFailed: false,
      userLoading: false,
      userError: "",
      typingTimer: null,

      // form
      verification_code: "",

      // otp
      otpLoading: false,
      otpTimer: 0,
      otpInterval: null,
      otpSent: false,

      // submit
      submitLoading: false,
      submitError: "",
      submitSuccess: "",
      successTimer: null,

      // Invite landing base
      landingBase: "https://h5.starmate.co.in",
    };
  },

  computed: {
    userImageToShow() {
      const url = this.userData?.profile_images?.[0]?.image_name;
      return !url || this.userImgFailed ? this.defaultUserImage : url;
    },

    disableOtpBtn() {
      return !this.userData?.profile_id || this.otpLoading || this.otpTimer > 0;
    },

    canSubmit() {
      const agencyOk = !!this.agency_profile_id;
      const userOk = !!this.userData?.profile_id;
      const otpOk = /^\d{6}$/.test(String(this.verification_code || "").trim());
      return agencyOk && userOk && otpOk;
    },

    // The agency API only returns a name and an id, so the avatar is the
    // initial rather than a photo.
    agencyName() {
      if (this.loadingAgency) return "Loading…";
      return this.agencyData?.name || "Agency not found";
    },

    agencyInitial() {
      const name = this.agencyData?.name || "";
      return name.trim().charAt(0) || "?";
    },

    agencyMeta() {
      if (this.loadingAgency) return "Fetching the invite…";
      if (!this.agencyData?.profile_id) return "This invite link is not valid";
      return `ID ${this.agencyData.profile_id}`;
    },

    otpBtnLabel() {
      if (this.otpTimer > 0) return this.formatOtpTime(this.otpTimer);
      return this.otpSent ? "Resend" : "Send code";
    },

    // Drives the stepper: which part of the form still needs work.
    currentStep() {
      if (!this.userData?.profile_id) return 1;
      return /^\d{6}$/.test(String(this.verification_code || "").trim()) ? 3 : 2;
    },
  },

  mounted() {
    this.agency_profile_id = this.$route.params.profile_id || null;
    this.fetchInviteAgencyData();
  },

  beforeUnmount() {
    clearTimeout(this.typingTimer);
    clearTimeout(this.successTimer);
    this.stopOtpTimer();
  },

  methods: {
    // ✅ ONLY NUMERIC INPUT FOR USER ID (paste-safe)
    onUserIdInput(e) {
      const digits = String(e?.target?.value || "").replace(/\D/g, "");
      this.user_profile_id = digits;

      // keep your existing debounce/search behavior
      this.onUserIdKeyup();
    },

    // ---------- helpers ----------
    clearOtp() {
      this.stopOtpTimer();
      this.otpTimer = 0;
      this.otpSent = false;
      this.otpLoading = false;
      this.verification_code = "";
    },

    clearMessages() {
      this.userError = "";
      this.submitError = "";
      this.submitSuccess = "";
      clearTimeout(this.successTimer);
    },

    clearUserResult() {
      this.userData = null;
      this.userImgFailed = false;
      this.clearOtp();
    },

    // ---------- agency ----------
    async fetchInviteAgencyData() {
      if (!this.agency_profile_id) return;

      this.loadingAgency = true;
      try {
        const res = await inviteAgencyApi.getAgencyDetails(this.agency_profile_id);
        this.agencyData = res?.data?.result || null;
      } catch (error) {
        console.error("Agency API Error =>", error);
        this.agencyData = null;
      } finally {
        this.loadingAgency = false;
      }
    },

    // ---------- user search ----------
    onUserIdKeyup() {
      const id = String(this.user_profile_id || "").trim();

      this.clearMessages();
      this.clearOtp(); // user typing => otp reset

      clearTimeout(this.typingTimer);

      if (!id) {
        this.clearUserResult();
        return;
      }

      if (id.length < 8) {
        // don’t nuke input, just clear the fetched user
        this.userData = null;
        this.userError = "";
        this.userImgFailed = false;
        return;
      }

      this.typingTimer = setTimeout(this.fetchUserDetails, 500);
    },

    async fetchUserDetails() {
      const id = String(this.user_profile_id || "").trim();
      if (id.length < 8) return;

      this.userLoading = true;
      this.userError = "";

      try {
        const res = await inviteAgencyApi.getUserDetails(id);

        const success = !!res?.data?.success;
        const result = res?.data?.result || null;
        const apiErrorMsg = res?.data?.error || res?.data?.message || "User not found";

        if (!success || !result) {
          this.userData = null;
          this.userError = apiErrorMsg;
          this.userImgFailed = false;
          return;
        }

        this.userData = { ...result, profile_id: result.profile_id || id };
        this.userImgFailed = false;
      } catch (e) {
        this.userData = null;
        this.userError = "API error. Try again.";
        this.userImgFailed = false;
      } finally {
        this.userLoading = false;
      }
    },

    // ---------- image fallback ----------
    onUserImgError(e) {
      if (e?.target?.src === this.defaultUserImage) return;
      this.userImgFailed = true;
      e.target.src = this.defaultUserImage;
    },

    // ---------- otp ----------
    async getVerificationCode() {
      if (this.disableOtpBtn) return;

      this.otpLoading = true;
      this.userError = "";

      try {
        const res = await inviteAgencyApi.sendOtp(this.userData.profile_id);

        if (res?.data?.success) {
          this.otpSent = false;
          this.startOtpTimer();
        } else {
          this.userError = res?.data?.error || res?.data?.message || "Failed to send OTP";
        }
      } catch (err) {
        console.error("OTP API Error:", err);
        this.userError = "OTP API error. Try again.";
      } finally {
        this.otpLoading = false;
      }
    },

    onOtpInput(e) {
      this.verification_code = String(e.target.value || "").replace(/\D/g, "").slice(0, 6);
    },

    formatOtpTime(sec) {
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      return `${m}:${String(s).padStart(2, "0")}`;
    },

    startOtpTimer() {
      this.stopOtpTimer();
      this.otpTimer = 600;

      this.otpInterval = setInterval(() => {
        this.otpTimer -= 1;
        if (this.otpTimer <= 0) {
          this.stopOtpTimer();
          this.otpSent = true;
        }
      }, 1000);
    },

    stopOtpTimer() {
      if (this.otpInterval) {
        clearInterval(this.otpInterval);
        this.otpInterval = null;
      }
    },

    // ---------- submit ----------
    async handleSubmit() {
      if (!this.canSubmit || this.submitLoading) return;

      this.submitLoading = true;
      this.submitError = "";
      this.submitSuccess = "";

      try {
        const payload = {
          agency_profile_id: this.agency_profile_id,
          user_profile_id: this.userData.profile_id,
          otp: String(this.verification_code || "").trim(),
        };

        const res = await inviteAgencyApi.updateUserFromInviteAgency(payload);

        if (!res?.data?.success) {
          this.submitError = res?.data?.error || res?.data?.message || "Invalid OTP or request failed";
          return;
        }

        // ✅ success message
        this.submitSuccess = res?.data?.message || "Successfully submitted";

        // ✅ reset form (keep success message visible)
        this.user_profile_id = "";
        this.clearUserResult();

        // ✅ redirect after few seconds
        clearTimeout(this.successTimer);
        this.successTimer = setTimeout(() => {
          window.location.href = `${this.landingBase}/become-agent-success`;
        }, 1000);
      } catch (err) {
        console.error("Submit API Error:", err);
        this.submitError = "Submit failed. Try again.";
      } finally {
        this.submitLoading = false;
      }
    },

    // ---------- close ----------
    // viewClose(status) {
    //   if (window.Android && typeof Android.viewClose === "function") {
    //     Android.viewClose(status);
    //   } else {
    //     window.parent.postMessage({ action: "closePopup", status }, "*");
    //   }
    // },
  },
};
</script>
